/* eslint-disable no-shadow,no-unused-expressions,immutable/no-mutation */
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import handleBgTabState from '../utils/handleBgTabState';
import { initialState as filtersInitial } from './board/filters';
import {
  AUTHENTICATION_TOKEN,
  COMMUNITY_ID,
  LOGGED_IN,
  ORGANIZATION_ID,
  PAPERWORK_TOKEN,
  REDIRECT_TO_PAGE,
  REGISTRATION_COMPLETED,
  REQUEST_PASSWORD_UPDATE,
} from '../constants/storageKeys';
import { clearDisclaimerInformingIds } from '../utils/localStorage/disclaimerInformingStorage';
import { checkIsPaperworkRoute } from '../utils/paperwork';

export const getPersistentState = () => {
  // On the paperwork route, only restore session state from a paperwork-scoped
  // token. If paperworkToken is absent the tab has no active paperwork session
  // and must start as logged-out, so it never inherits — and then corrupts —
  // a regular-app session that belongs to another tab.
  const { isPaperworkRoute } = checkIsPaperworkRoute();
  const hasPaperworkToken = !!window.storage.getItem(PAPERWORK_TOKEN);
  const isPaperworkTabWithoutSession = isPaperworkRoute && !hasPaperworkToken;

  const authenticationToken = isPaperworkTabWithoutSession
    ? null
    : window.storage.getItem(AUTHENTICATION_TOKEN);
  const loggedIn = isPaperworkTabWithoutSession
    ? null
    : window.storage.getItem(LOGGED_IN);
  const storedUser = window.storage.getItem('user');
  const organizationId = window.storage.getItem(ORGANIZATION_ID);
  const communityId = window.storage.getItem(COMMUNITY_ID);
  const isFamilyMemberInvitation = window.storage.getItem(
    'isFamilyMemberInvitation',
  );
  const isCommunityMemberInvitation = window.storage.getItem(
    'isCommunityMemberInvitation',
  );
  const invitationType = window.storage.getItem('invitationType');
  const invitationMethod = window.storage.getItem('invitationMethod');
  const user = storedUser ? JSON.parse(storedUser) : {};
  const { id } = user;
  const boardFilters = id && window.storage.getItem(`board-filters-${id}`);
  const savedSurveys = id && window.storage.getItem(`saved-surveys-${id}`);
  const teamId = window.storage.getItem('selectedTeamId');
  const teamUserId = window.storage.getItem('actingUserId');
  const teamUser = teamUserId ? { id: JSON.parse(teamUserId) } : null;
  const requestPasswordUpdate = window.storage.getItem(REQUEST_PASSWORD_UPDATE);
  const registrationCompleted = window.storage.getItem(REGISTRATION_COMPLETED);

  const state = {
    auth: {
      status: {
        authenticationToken: authenticationToken
          ? JSON.parse(authenticationToken)
          : null,
        loggedIn: loggedIn ? JSON.parse(loggedIn) : null,
        organizationId: organizationId ? JSON.parse(organizationId) : null,
        communityId: communityId ? JSON.parse(communityId) : null,
        isFamilyMemberInvitation: isFamilyMemberInvitation
          ? JSON.parse(isFamilyMemberInvitation)
          : null,
        isCommunityMemberInvitation: isCommunityMemberInvitation
          ? JSON.parse(isCommunityMemberInvitation)
          : null,
        invitationType: invitationType ? JSON.parse(invitationType) : null,
        invitationMethod: invitationMethod
          ? JSON.parse(invitationMethod)
          : null,
        requestPasswordUpdate: requestPasswordUpdate
          ? JSON.parse(requestPasswordUpdate)
          : false,
        registrationCompleted: registrationCompleted
          ? JSON.parse(registrationCompleted)
          : false,
      },
    },
    board: {},
    context: {
      loggedUser: user,
      teamUser: {
        selectedTeamId: teamId ? JSON.parse(teamId) : null,
        user: teamUser,
      },
    },
    surveys: {
      saved: savedSurveys ? JSON.parse(savedSurveys) : null,
    },
  };

  if (boardFilters) {
    const filters = JSON.parse(boardFilters);

    state.board.filters = {
      ...filtersInitial,
      ...filters,
      searchQuery: filtersInitial.searchQuery,
    };
  }

  return state;
};

export const initializePersistingState = (store, persistentState) => {
  // add handling two tabs activity flow
  let focused = true;
  /* eslint-disable prettier/prettier */
  window.addEventListener(
    'focus',
    () => {
      focused = true;
    },
    false,
  );
  window.addEventListener(
    'blur',
    () => {
      focused = false;
    },
    false,
  );
  window.addEventListener(
    'storage',
    e => handleBgTabState(e, focused, store),
    false,
  );
  /* eslint-enable prettier/prettier */
  const {
    auth: {
      status: {
        authenticationToken,
        loggedIn,
        organizationId,
        communityId,
        isFamilyMemberInvitation,
        isCommunityMemberInvitation,
        invitationType,
        invitationMethod,
        requestPasswordUpdate,
        registrationCompleted,
      },
    },
    context: { loggedUser },
    board,
    surveys,
    profiles,
  } = persistentState;
  let previousAuthToken = authenticationToken;
  let previousLoggedIn = loggedIn;
  let previousUser = loggedUser;
  let previousOrgId = organizationId;
  let previousCommunityId = communityId;
  let previousIsFamilyMemberInvitation = isFamilyMemberInvitation;
  let previousIsCommunityMemberInvitation = isCommunityMemberInvitation;
  let previousInvitationType = invitationType;
  let previousInvitationMethod = invitationMethod;
  let previousBoardFilters = board && board.filters;
  let previousSavedSurveys = surveys && surveys.saved;
  let previousRequestPasswordUpdate = requestPasswordUpdate;
  let previousRegistrationCompleted = registrationCompleted;
  let previousFieldsEditable = get(profiles, 'profileEditable.fieldsEditable');

  store.subscribe(() => {
    const {
      auth: {
        status: {
          authenticationToken,
          loggedIn,
          organizationId,
          communityId,
          isFamilyMemberInvitation,
          isCommunityMemberInvitation,
          invitationType,
          invitationMethod,
          requestPasswordUpdate,
          registrationCompleted,
        },
      },
      context: { loggedUser },
      board,
      surveys,
      profiles,
    } = store.getState();

    const boardFilters = board ? board.filters : null;
    const savedSurveys = surveys ? surveys.saved : null;
    const fieldsEditable = get(profiles, 'profileEditable.fieldsEditable');

    // update localstorage if some needed data has changed

    // Paperwork tabs use a dedicated paperworkToken and must not overwrite the
    // shared authenticationToken / loggedIn keys — doing so would corrupt the
    // regular-app session running in another tab.
    const { isPaperworkRoute } = checkIsPaperworkRoute();

    if (
      !isPaperworkRoute &&
      authenticationToken &&
      authenticationToken !== previousAuthToken
    ) {
      window.storage.setItem(
        AUTHENTICATION_TOKEN,
        JSON.stringify(authenticationToken),
      );
    }
    if (!isPaperworkRoute && loggedIn !== previousLoggedIn) {
      window.storage.setItem(LOGGED_IN, JSON.stringify(loggedIn));
    }
    if (organizationId && !previousOrgId) {
      window.storage.setItem(ORGANIZATION_ID, JSON.stringify(organizationId));
    }
    if (communityId && !previousCommunityId) {
      window.storage.setItem(COMMUNITY_ID, JSON.stringify(communityId));
    }
    if (isFamilyMemberInvitation && !previousIsFamilyMemberInvitation) {
      window.storage.setItem(
        'isFamilyMemberInvitation',
        JSON.stringify(isFamilyMemberInvitation),
      );
    }
    if (isCommunityMemberInvitation && !previousIsCommunityMemberInvitation) {
      window.storage.setItem(
        'isCommunityMemberInvitation',
        JSON.stringify(isCommunityMemberInvitation),
      );
    }
    if (invitationType && !previousInvitationType) {
      window.storage.setItem('invitationType', JSON.stringify(invitationType));
    }
    if (invitationMethod && !previousInvitationMethod) {
      window.storage.setItem(
        'invitationMethod',
        JSON.stringify(invitationMethod),
      );
    }
    if (requestPasswordUpdate !== previousRequestPasswordUpdate) {
      window.storage.setItem(
        REQUEST_PASSWORD_UPDATE,
        JSON.stringify(requestPasswordUpdate),
      );
    }

    if (registrationCompleted !== previousRegistrationCompleted) {
      window.storage.setItem(
        REGISTRATION_COMPLETED,
        JSON.stringify(registrationCompleted),
      );
    }

    if (!isEmpty(loggedUser) && loggedUser !== previousUser) {
      window.storage.setItem('user', JSON.stringify(loggedUser));

      const id = get(loggedUser, 'id');
      const patientId = get(loggedUser, 'primaryPatientProfile.id');
      const providerId = get(loggedUser, 'primaryProviderProfile.id');

      id && window.storage.setItem('id', JSON.stringify(id));
      patientId &&
        window.storage.setItem('patientId', JSON.stringify(patientId));
      providerId &&
        window.storage.setItem('providerId', JSON.stringify(providerId));
    }

    if (loggedIn && !isEqual(boardFilters, previousBoardFilters)) {
      const id = get(loggedUser, 'id');
      id &&
        window.storage.setItem(
          `board-filters-${id}`,
          JSON.stringify(boardFilters),
        );
    }

    if (!isEqual(savedSurveys, previousSavedSurveys)) {
      const id = get(loggedUser, 'id');
      id &&
        window.storage.setItem(
          `saved-surveys-${id}`,
          JSON.stringify(savedSurveys),
        );
    }

    if (fieldsEditable !== previousFieldsEditable) {
      window.storage.setItem('fieldsEditable', fieldsEditable);
    }

    previousAuthToken = authenticationToken;
    previousLoggedIn = loggedIn;
    previousUser = loggedUser;
    previousOrgId = organizationId;
    previousCommunityId = communityId;
    previousIsFamilyMemberInvitation = isFamilyMemberInvitation;
    previousIsCommunityMemberInvitation = isCommunityMemberInvitation;
    previousInvitationType = invitationType;
    previousInvitationMethod = invitationMethod;
    previousBoardFilters = boardFilters;
    previousSavedSurveys = savedSurveys;
    previousRequestPasswordUpdate = requestPasswordUpdate;
    previousFieldsEditable = fieldsEditable;
    previousRegistrationCompleted = registrationCompleted;
  });
};

export const clearLocalStorage = ({ preservePaperwork = false } = {}) => {
  Object.keys(window.storage).forEach(i => {
    const fontItem = i.includes('x-font');
    const boardFilters = i.includes('board-filters-');
    const savedSurveys = i.includes('saved-surveys-');
    const redirectToPage = i.includes(REDIRECT_TO_PAGE);
    // Preserve the paperwork session token when a regular-app tab logs out so
    // that an open paperwork tab in another window is not disrupted.
    const paperworkToken = preservePaperwork && i === PAPERWORK_TOKEN;

    !(
      fontItem ||
      boardFilters ||
      savedSurveys ||
      redirectToPage ||
      paperworkToken
    ) && window.storage.removeItem(i);
  });

  clearDisclaimerInformingIds();
};



// WEBPACK FOOTER //
// ./src/redux/persistent-state.js